import Link from 'next/link';
import type { ComponentProps, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'on-ink' | 'quiet';
type Size = 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-2 rounded-lg font-semibold whitespace-nowrap transition-colors duration-150 ease-out-soft';

const variants: Record<Variant, string> = {
  /** The one filled Dispatch Blue action on a surface. */
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 focus-ring',
  /** Tinted companion to `primary`, never outlined (DESIGN.md). */
  secondary: 'bg-brand-25 text-brand-800 hover:bg-brand-50 active:bg-brand-100 focus-ring',
  /** Secondary action on an ink band. */
  'on-ink': 'bg-white/10 text-on-ink ring-1 ring-inset ring-white/20 hover:bg-white/15 focus-ring-on-ink',
  /** Text-weight link with an arrow, for "read more" actions. */
  quiet: 'text-brand-700 hover:text-brand-800 underline-offset-4 hover:underline focus-ring rounded-sm',
};

const sizes: Record<Size, string> = {
  md: 'min-h-11 px-4 text-[15px]',
  lg: 'min-h-12 px-5 text-base',
};

export function buttonClass(variant: Variant = 'primary', size: Size = 'md') {
  return `${base} ${variants[variant]} ${variant === 'quiet' ? 'text-[15px]' : sizes[size]}`;
}

type ButtonLinkProps = Omit<ComponentProps<typeof Link>, 'className'> & {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: ReactNode;
};

/** A link styled as a button. Every call-to-action on the site is navigation, so this is the only button shape. */
export function ButtonLink({ variant = 'primary', size = 'md', className = '', children, ...rest }: ButtonLinkProps) {
  return (
    <Link {...rest} className={`${buttonClass(variant, size)} ${className}`}>
      {children}
    </Link>
  );
}
